import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Footer from '../footer';
import SEO from '../../util/SEO';
import './services.css';

/**
 * Reusable Service Page Layout
 * Provides hero, features, process, why-choose-us, tech stack, FAQ, and CTA sections.
 */
const ServicePageLayout = ({
  accentClass,
  badge,
  title,
  description,
  ctaLabel = 'Get a Free Quote',
  features = [],
  process = [],
  whyChooseUs = [],
  techStack = [],
  faqs = [],
  seo = {},
  structuredData = null,
}) => {
  const { t } = useTranslation();
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = useCallback((index) => {
    setActiveFaq((prev) => (prev === index ? null : index));
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const elements = document.querySelectorAll('.service-animate');
    elements.forEach((el) => observer.observe(el));

    return () => elements.forEach((el) => observer.unobserve(el));
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
    <main className={`service-container ${accentClass}`}>
      <SEO
        title={seo.title || title}
        description={seo.description || description}
        canonical={seo.canonical}
        keywords={seo.keywords}
        structuredData={structuredData}
      />

      {/* ─── HERO ─── */}
      <section className="service-hero" id="service-hero" aria-labelledby="service-page-title">
        <p className="service-hero-badge">
          <span className="service-hero-badge-dot" aria-hidden="true" />
          {badge}
        </p>
        <h1 id="service-page-title" className="service-hero-title">{title}</h1>
        <p className="service-hero-description">{description}</p>
        <a href="mailto:info@bringitgo.tech?subject=Inquiry" className="service-hero-cta">
          {ctaLabel}
          <span className="service-hero-cta-arrow" aria-hidden="true">-&gt;</span>
        </a>
      </section>

      <div className="service-content">
        {/* ─── FEATURES ─── */}
        {features.length > 0 && (
          <section className="service-features-section service-animate" id="service-features" aria-labelledby="service-features-title">
            <h2 id="service-features-title" className="service-section-title">{t('serviceLayout.features.title')}</h2>
            <p className="service-section-subtitle">
              {t('serviceLayout.features.subtitle')}
            </p>
            <div className="service-features-grid">
              {features.map((feature, i) => (
                <article
                  key={i}
                  className={`service-feature-card service-animate service-animate-delay-${i + 1}`}
                >
                  <div className="service-feature-icon" aria-hidden="true">{feature.icon}</div>
                  <h3 className="service-feature-title">{feature.title}</h3>
                  <p className="service-feature-description">{feature.description}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ─── PROCESS ─── */}
        {process.length > 0 && (
          <section className="service-process-section service-animate" id="service-process" aria-labelledby="service-process-title">
            <h2 id="service-process-title" className="service-section-title">{t('serviceLayout.process.title')}</h2>
            <p className="service-section-subtitle">
              {t('serviceLayout.process.subtitle')}
            </p>
            <ol className="service-process-grid">
              {process.map((step, i) => (
                <li key={i} className={`service-process-step service-animate service-animate-delay-${i + 1}`}>
                  <div className="service-process-number" aria-hidden="true">{String(i + 1).padStart(2, '0')}</div>
                  <h3 className="service-process-title">{step.title}</h3>
                  <p className="service-process-desc">{step.description}</p>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* ─── WHY CHOOSE US ─── */}
        {whyChooseUs.length > 0 && (
          <section className="service-why-section service-animate" id="service-why" aria-labelledby="service-why-title">
            <h2 id="service-why-title" className="service-section-title">{t('serviceLayout.whyChooseUs.title')}</h2>
            <p className="service-section-subtitle">
              {t('serviceLayout.whyChooseUs.subtitle')}
            </p>
            <div className="service-why-grid">
              {whyChooseUs.map((item, i) => (
                <article key={i} className={`service-why-card service-animate service-animate-delay-${i + 1}`}>
                  <div className="service-why-number">{item.stat}</div>
                  <div className="service-why-title">{item.label}</div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ─── TECH STACK ─── */}
        {techStack.length > 0 && (
          <section className="service-tech-section service-animate" id="service-tech" aria-labelledby="service-tech-title">
            <h2 id="service-tech-title" className="service-section-title">{t('serviceLayout.techStack.title')}</h2>
            <p className="service-section-subtitle">
              {t('serviceLayout.techStack.subtitle')}
            </p>
            <ul className="service-tech-grid">
              {techStack.map((tech, i) => (
                <li key={i} className="service-tech-badge">
                  <span className="service-tech-badge-icon" aria-hidden="true">{tech.icon}</span>
                  {tech.name}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ─── FAQ ─── */}
        {faqs.length > 0 && (
          <section className="service-faq-section service-animate" id="service-faq" aria-labelledby="service-faq-title">
            <h2 id="service-faq-title" className="service-section-title">{t('serviceLayout.faq.title')}</h2>
            <p className="service-section-subtitle">
              {t('serviceLayout.faq.subtitle')}
            </p>
            <div className="service-faq-container">
              {faqs.map((faq, i) => (
                <article
                  key={i}
                  className={`service-faq-item ${activeFaq === i ? 'active' : ''}`}
                >
                  <button
                    className="service-faq-header"
                    type="button"
                    onClick={() => toggleFaq(i)}
                    aria-expanded={activeFaq === i}
                    aria-controls={`service-faq-answer-${i}`}
                  >
                    <h3 className="service-faq-title">{faq.question}</h3>
                    <span className="service-faq-icon" aria-hidden="true">v</span>
                  </button>
                  <div
                    id={`service-faq-answer-${i}`}
                    className="service-faq-content"
                    aria-hidden={activeFaq !== i}
                  >
                    <p className="service-faq-answer">{faq.answer}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ─── CTA ─── */}
        <section className="service-cta-section service-animate" id="service-cta" aria-labelledby="service-cta-title">
          <h2 id="service-cta-title" className="service-cta-title">{t('serviceLayout.cta.title')}</h2>
          <p className="service-cta-description">
            {t('serviceLayout.cta.description')}
          </p>
          <div className="service-cta-buttons">
            <a href="mailto:info@bringitgo.tech?subject=Project Inquiry" className="service-cta-btn-primary">
              {t('serviceLayout.cta.primaryBtn')}
            </a>
            <a href="/portfolio" className="service-cta-btn-secondary">
              {t('serviceLayout.cta.secondaryBtn')}
            </a>
          </div>
        </section>
      </div>

    </main>
    </>
  );
};

export default ServicePageLayout;
